import React from "react";
import ReactDOM from "react-dom";
import icons from "../../../../../assets/images/icons";
import { useEscapeClose } from "../../../../../common/hooks/useEscapeClose";
import styles from "./ConfirmModal.module.scss";

/**
 * 확인 모달 통합 컴포넌트 (LeaveConfirmModal + ReportConfirmModal 통합)
 * - type="leave"  : 채팅방 나가기 (Figma 40004311:87331)
 * - type="report" : 신고/차단 확인 (Figma 40004313:36944)
 * title, description, confirmLabel, cancelLabel 으로 개별 오버라이드 가능
 */

const TYPE_DEFAULTS = {
  leave: {
    title: "채팅방을 정말 나가시겠습니까?",
    description: "채팅방을 나가면 해당 정보는 모두 삭제됩니다.",
    confirmLabel: "나가기",
    cancelLabel: "취소",
    nodeId: "40004311:87331",
  },
  report: {
    title: "이 사용자를 신고하시겠습니까?",
    description: "신고된 내용은 검토 후 적절히 처리됩니다.",
    confirmLabel: "신고하기",
    cancelLabel: "취소",
    nodeId: "40004313:36944",
  },
  block: {
    title: "이 사용자를 차단하시겠습니까?",
    description: "차단하면 해당 사용자로부터 더 이상 메시지를 받지 않게 됩니다.",
    confirmLabel: "차단하기",
    cancelLabel: "취소",
    nodeId: "40004313:42854",
  },
};

function ConfirmModal({
  type,
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
}) {
  useEscapeClose(onClose, open);

  if (!open) return null;

  const defaults = TYPE_DEFAULTS[type] ?? TYPE_DEFAULTS.leave;
  const resolvedTitle = title ?? defaults.title;
  const resolvedDescription = description !== undefined ? description : defaults.description;
  const resolvedConfirm = confirmLabel ?? defaults.confirmLabel;
  const resolvedCancel = cancelLabel ?? defaults.cancelLabel;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        data-name="Modal"
        data-node-id={defaults.nodeId}
      >
        <div className={styles.nav}>
          <div className={styles.closeWrap}>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              <img src={icons.messager?.close} alt="" className={styles.closeIcon} />
            </button>
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.contents}>
            <div className={styles.heading}>
              <div className={styles.iconLarge}>
                <img src={icons.messager?.circleExclamation} alt="" className={styles.iconImg} />
              </div>
              <div className={styles.textBlock}>
                <h2 id="confirm-modal-title" className={styles.title}>
                  {resolvedTitle}
                </h2>
                {resolvedDescription ? (
                  <p className={styles.description}>{resolvedDescription}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actionArea}>
          <div className={styles.actions}>
            <button type="button" className={styles.primaryBtn} onClick={onConfirm}>
              {resolvedConfirm}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={onClose}>
              {resolvedCancel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmModal;
